import React, { useState } from 'react';
import {
  Menu,
  Box,
  MenuItem,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import Groups3OutlinedIcon from '@mui/icons-material/Groups3Outlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import Person4OutlinedIcon from '@mui/icons-material/Person4Outlined';
import ListOutlinedIcon from '@mui/icons-material/ListOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';

import styles from './NavigationBar.module.scss';
import { NavigationRoutes } from '../constants/navigationConsts';
import { useSessionData } from '../providers';
import AppearanceSelect from './AppearanceSelect';
import { MOTIVISION_ADMINS } from '../constants/admins';
import AccountIcon from './composites/AccountIcon';
import AssetThumbnail from './atoms/AssetThumbnail';

interface NavigationRouteConfig {
  navigation: NavigationRoutes;
  title: string;
}

export interface NavigationBarProps {
  navigationRoute: NavigationRoutes | null;
  navigationRoutes: NavigationRouteConfig[];
  setNavigationRoute: (navigationRoutes: NavigationRoutes) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const routeIconsMap: Record<NavigationRoutes, () => React.ReactNode> = {
  [NavigationRoutes.LiveWorkouts]: () => <FitnessCenterIcon />,
  [NavigationRoutes.LiveDemo]: () => <FitnessCenterOutlinedIcon />,
  [NavigationRoutes.Exercises]: () => <DirectionsRunOutlinedIcon />,
  [NavigationRoutes.Clients]: () => <PersonOutlineOutlinedIcon />,
  [NavigationRoutes.Managers]: () => <Groups3OutlinedIcon />,
  [NavigationRoutes.Workouts]: () => <FitnessCenterOutlinedIcon />,
  [NavigationRoutes.Programs]: () => <ListOutlinedIcon />,
  [NavigationRoutes.Groups]: () => <GroupsOutlinedIcon />,
  [NavigationRoutes.Trainers]: () => <Person4OutlinedIcon />,
  [NavigationRoutes.GymSettings]: () => <SettingsSuggestIcon />,
  [NavigationRoutes.Gyms]: () => <SettingsSuggestIcon />,
  [NavigationRoutes.StationsHealth]: () => <SettingsSuggestIcon />,
};

const NavigationBar: React.FC<NavigationBarProps> = ({
  navigationRoutes,
  navigationRoute,
  setNavigationRoute,
  collapsed = false,
  onToggleCollapse,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const { gym, loginManager } = useSessionData();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      handleMenuClose();
    } else {
      handleMenuOpen(event);
    }
  };

  const renderNavigationContents = (options?: {
    onSelect?: () => void;
    className?: string;
  }) => {
    const navListClassName = [styles.navList, options?.className]
      .filter(Boolean)
      .join(' ');
    return (
      <Box className={navListClassName}>
        {navigationRoutes.map(({ navigation, title }) => (
          <label
            key={navigation}
            className={`${styles.navItem} ${navigation === navigationRoute
                ? styles.selectedNav
                : styles.unselectedNav
              }`}
          >
            <input
              type="radio"
              value={navigation}
              checked={navigation === navigationRoute}
              onChange={() => {
                setNavigationRoute(navigation);
                options?.onSelect?.();
              }}
              style={{ display: 'none' }}
            />
            <Tooltip
              title={collapsed ? title : ''}
              placement="right"
              arrow
              disableHoverListener={!collapsed}
              slotProps={{
                tooltip: { className: styles.collapsedTooltip },
                arrow: { className: styles.collapsedTooltipArrow },
              }}
            >
              <span className={styles.navItemInner}>
                {routeIconsMap[navigation]()}
                <span className={styles.navLabel}>{title}</span>
              </span>
            </Tooltip>
          </label>
        ))}
      </Box>
    );
  };

  const managerId = loginManager?.id;
  const canManageAppearance =
    typeof managerId === 'string' && MOTIVISION_ADMINS.includes(managerId);

  return (
    <div
      className={`${styles.navigationBar} ${collapsed ? styles.collapsed : ''}`}
    >
      {!isMobile && (
        <div className={styles.logo}>
          <img
            className={styles.logoImage}
            src="/motivision-logo.png"
            alt="Motivision"
          />
        </div>
      )}
      {!isMobile && !collapsed && canManageAppearance && (
        <div className={styles.appearanceWrapper}>
          <AppearanceSelect />
        </div>
      )}
      {!isMobile && !collapsed && (
        <div className={styles.gymInfo}>
          <AssetThumbnail
            className={styles.gymLogo}
            src={gym?.logoUrl}
            context="gym"
            fit="contain"
            alt={`${gym?.name || 'Gym'} logo`}
          />
          <div className={styles.gymName}>{gym?.name || 'Gym'}</div>
        </div>
      )}
      {!isMobile && (
        <div className={styles.collapseToggleWrapper}>
          <IconButton
            className={styles.collapseToggle}
            size="small"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRightIcon
                className={styles.collapseIcon}
                fontSize="small"
              />
            ) : (
              <ChevronLeftIcon
                className={styles.collapseIcon}
                fontSize="small"
              />
            )}
          </IconButton>
        </div>
      )}
      <div className={styles.navigationBarContent}>
        {renderNavigationContents()}
      </div>
      <IconButton
        className={styles.menuButton}
        aria-label="menu"
        size="large"
        onClick={handleMenuToggle}
      >
        <MenuIcon />
      </IconButton>
      {isMobile && (
        <div className={styles.mobileAccountIcon}>
          <AccountIcon />
        </div>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        className={styles.responsiveMenu}
      >
        {isMobile ? (
          <div className={styles.mobileMenuContent}>
            {renderNavigationContents({
              onSelect: handleMenuClose,
              className: styles.mobileMenuNav,
            })}
          </div>
        ) : (
          <MenuItem onClick={handleMenuClose}>
            {renderNavigationContents()}
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default NavigationBar;
